// src/pages/reports/DrmStbManagement.tsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Checkbox,
  FormControlLabel,
  Button,
  TextField,
  Pagination,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Divider
} from "@mui/material";
import {
  Sync as SyncIcon,
  CloudUpload as ImportIcon,
  GetApp as ExportIcon,
  Search as SearchIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Fingerprint as FingerprintIcon,
  Message as MessageIcon,
  Update as UpdateIcon,
  Lock as LockIcon,
  Wifi as WifiIcon,
  Apps as AppsIcon,
  Storage as StorageIcon,
  Language as LanguageIcon,
  Download as DownloadIcon
} from "@mui/icons-material";
import { authenticationService } from "src/services/authentication.service";
import { hasPermission } from "src/components/Permissions/hasPermission";
import dayjs from 'dayjs';
import reportDownloadService from 'src/services/reportDownload.service';

// Mock Data for STB configurations
const stbData = [
  {
    id: 1,
    stbId: "STB-789XYZ",
    macId: "AB:CD:EF:12:34",
    drmProtection: "Enabled",
    decryption: "Supported",
    fingerprintSupport: "Both",
    addressability: "Individual",
    messaging: "Supported (120 chars)",
    bisCompliance: "Compliant",
    otaUpgrade: "Supported",
    copyProtection: "Enabled",
    fingerprintAlwaysOn: "Yes",
    geoTagged: "Yes",
    commandSource: "Direct from DRM",
    thirdPartyApps: "Blocked",
    keyAccess: "IPTV Only",
    playStoreAccess: "Blocked",
    lastConfigUpdate: "2023-04-20 14:25",
    complianceStatus: "Compliant"
  },
  {
    id: 2,
    stbId: "STB-456ABC",
    macId: "BC:DE:FA:23:45",
    drmProtection: "Enabled",
    decryption: "Supported",
    fingerprintSupport: "Global Only",
    addressability: "Group",
    messaging: "Supported (100 chars)",
    bisCompliance: "Compliant",
    otaUpgrade: "Supported",
    copyProtection: "Disabled",
    fingerprintAlwaysOn: "Yes",
    geoTagged: "No",
    commandSource: "Via Middleware",
    thirdPartyApps: "Allowed",
    keyAccess: "All Networks",
    playStoreAccess: "Allowed",
    lastConfigUpdate: "2023-04-19 10:15",
    complianceStatus: "Non-Compliant"
  },
  {
    id: 3,
    stbId: "STB-123DEF",
    macId: "CD:EF:12:34:56",
    drmProtection: "Enabled",
    decryption: "Supported",
    fingerprintSupport: "Targeted Only",
    addressability: "Individual",
    messaging: "Supported (150 chars)",
    bisCompliance: "Pending",
    otaUpgrade: "Not Supported",
    copyProtection: "Enabled",
    fingerprintAlwaysOn: "No",
    geoTagged: "Yes",
    commandSource: "Direct from DRM",
    thirdPartyApps: "Blocked",
    keyAccess: "IPTV Only",
    playStoreAccess: "Blocked",
    lastConfigUpdate: "2023-04-21 09:45",
    complianceStatus: "Partially Compliant"
  },
  {
    id: 4,
    stbId: "STB-789GHI",
    macId: "DE:FA:23:45:67",
    drmProtection: "Enabled",
    decryption: "Supported",
    fingerprintSupport: "Both",
    addressability: "Individual",
    messaging: "Supported (120 chars)",
    bisCompliance: "Compliant",
    otaUpgrade: "Supported",
    copyProtection: "Enabled",
    fingerprintAlwaysOn: "Yes",
    geoTagged: "Yes",
    commandSource: "Direct from DRM",
    thirdPartyApps: "Blocked",
    keyAccess: "IPTV Only",
    playStoreAccess: "Blocked",
    lastConfigUpdate: "2023-04-20 16:20",
    complianceStatus: "Compliant"
  },
  {
    id: 5,
    stbId: "STB-456JKL",
    macId: "EF:12:34:56:78",
    drmProtection: "Disabled",
    decryption: "Not Supported",
    fingerprintSupport: "None",
    addressability: "Group",
    messaging: "Not Supported",
    bisCompliance: "Non-Compliant",
    otaUpgrade: "Not Supported",
    copyProtection: "Disabled",
    fingerprintAlwaysOn: "No",
    geoTagged: "No",
    commandSource: "None",
    thirdPartyApps: "Allowed",
    keyAccess: "All Networks",
    playStoreAccess: "Allowed",
    lastConfigUpdate: "2023-04-18 08:30",
    complianceStatus: "Non-Compliant"
  },
  {
    id: 6,
    stbId: "STB-123MNO",
    macId: "12:34:56:78:90",
    drmProtection: "Enabled",
    decryption: "Supported",
    fingerprintSupport: "Both",
    addressability: "Individual",
    messaging: "Supported (120 chars)",
    bisCompliance: "Compliant",
    otaUpgrade: "Supported",
    copyProtection: "Enabled",
    fingerprintAlwaysOn: "Yes",
    geoTagged: "Yes",
    commandSource: "Direct from DRM",
    thirdPartyApps: "Blocked",
    keyAccess: "IPTV Only",
    playStoreAccess: "Blocked",
    lastConfigUpdate: "2023-04-22 11:15",
    complianceStatus: "Compliant"
  }
];

// Define columns for the report download service
const columns = [
  { field: 'stbId', headerName: 'Set Top Box Identifier', minWidth: 120 },
  { field: 'macId', headerName: 'Media Access Control Address', minWidth: 150 },
  { field: 'drmProtection', headerName: 'DRM content protection status', minWidth: 140 },
  { field: 'decryption', headerName: 'Content decryption capability', minWidth: 120 },
  { field: 'fingerprintSupport', headerName: 'Fingerprinting support type', minWidth: 120 },
  { field: 'addressability', headerName: 'Individual addressability from Head-end', minWidth: 140 },
  { field: 'messaging', headerName: 'Messaging support and character length', minWidth: 150 },
  { field: 'bisCompliance', headerName: 'Bureau of Indian Standards compliance', minWidth: 140 },
  { field: 'otaUpgrade', headerName: 'Over-the-air software upgrade capability', minWidth: 140 },
  { field: 'copyProtection', headerName: 'Copy protection system status', minWidth: 120 },
  { field: 'fingerprintAlwaysOn', headerName: 'Fingerprinting never disabled', minWidth: 120 },
  { field: 'geoTagged', headerName: 'Geo-tagging capability', minWidth: 120 },
  { field: 'commandSource', headerName: 'Command reception source', minWidth: 150 },
  { field: 'thirdPartyApps', headerName: 'Third party app download status', minWidth: 140 },
  { field: 'keyAccess', headerName: 'Authorization key access source', minWidth: 140 },
  { field: 'playStoreAccess', headerName: 'Play Store access status', minWidth: 120 },
  { field: 'complianceStatus', headerName: 'Overall regulatory compliance status', minWidth: 120 },
];

const DrmStbManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [stbSettings, setStbSettings] = useState({
    drmProtection: true,
    decryption: true,
    fingerprintSupport: "both",
    addressability: "individual",
    messagingSupport: true,
    messagingLength: 120,
    bisCompliance: true,
    otaUpgrade: true,
    copyProtection: true,
    fingerprintAlwaysOn: true,
    geoTagging: true,
    directCommands: true,
    blockThirdParty: true,
    restrictKeyAccess: true,
    blockPlayStore: true,
    scrollMessaging: true,
    messagingPosition: "bottom",
    watermarkPosition: "encoder"
  });

  const rowsPerPage = 5;
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  // Filter data based on search term
  const filteredData = stbData.filter((row) =>
    Object.values(row).some(
      (val) => val.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Pagination
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Table headers
  const headers = [
    { id: 'stbId', display: 'STB ID', full: 'Set Top Box Identifier', width: '120px' },
    { id: 'macId', display: 'MAC ID', full: 'Media Access Control Address', width: '150px' },
    { id: 'drmProtection', display: 'DRM Protection', full: 'DRM content protection status', width: '140px' },
    { id: 'decryption', display: 'Decryption', full: 'Content decryption capability', width: '120px' },
    { id: 'fingerprintSupport', display: 'Fingerprint', full: 'Fingerprinting support type', width: '120px' },
    { id: 'addressability', display: 'Addressability', full: 'Individual addressability from Head-end', width: '140px' },
    { id: 'messaging', display: 'Messaging', full: 'Messaging support and character length', width: '150px' },
    { id: 'bisCompliance', display: 'BIS Compliance', full: 'Bureau of Indian Standards compliance', width: '140px' },
    { id: 'otaUpgrade', display: 'OTA Upgrade', full: 'Over-the-air software upgrade capability', width: '140px' },
    { id: 'copyProtection', display: 'Copy Protect', full: 'Copy protection system status', width: '120px' },
    { id: 'fingerprintAlwaysOn', display: 'FP Always On', full: 'Fingerprinting never disabled', width: '120px' },
    { id: 'geoTagged', display: 'Geo Tagged', full: 'Geo-tagging capability', width: '120px' },
    { id: 'commandSource', display: 'Command Source', full: 'Command reception source', width: '150px' },
    { id: 'thirdPartyApps', display: '3rd Party Apps', full: 'Third party app download status', width: '140px' },
    { id: 'keyAccess', display: 'Key Access', full: 'Authorization key access source', width: '140px' },
    { id: 'playStoreAccess', display: 'Play Store', full: 'Play Store access status', width: '120px' },
    { id: 'complianceStatus', display: 'Compliance', full: 'Overall regulatory compliance status', width: '120px' },
  ];

  // Single download handler for both formats using common service
  const handleDownload = async (format: 'pdf' | 'excel') => {
    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      const complianceStats = {
        compliant: filteredData.filter(row => row.complianceStatus === 'Compliant').length,
        nonCompliant: filteredData.filter(row => row.complianceStatus === 'Non-Compliant').length,
        partial: filteredData.filter(row => row.complianceStatus === 'Partially Compliant').length,
        pending: filteredData.filter(row => row.complianceStatus === 'Pending' || row.bisCompliance === 'Pending').length,
      };

      // Generate custom filename
      const customFileName = `STB_Management_Report_${dayjs().format('YYYY-MM-DD_HHmmss')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      const success = await reportDownloadService.downloadReport({
        title: 'STB/Consumer Subscription Management Report',
        columns,
        rows: filteredData,
        fileName: customFileName,
       
        // Excel specific configuration - using full headers
        excelHeaders: headers.map(header => header.full),
        excelRowMapper: (row: any) => [
          row.stbId || '',
          row.macId || '',
          row.drmProtection || '',
          row.decryption || '',
          row.fingerprintSupport || '',
          row.addressability || '',
          row.messaging || '',
          row.bisCompliance || '',
          row.otaUpgrade || '',
          row.copyProtection || '',
          row.fingerprintAlwaysOn || '',
          row.geoTagged || '',
          row.commandSource || '',
          row.thirdPartyApps || '',
          row.keyAccess || '',
          row.playStoreAccess || '',
          row.complianceStatus || ''
        ]
      }, format);
      
      if (!success) {
        alert(`Failed to generate ${format.toUpperCase()}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error generating ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  const handleSettingChange = (setting) => (event) => {
    setStbSettings({
      ...stbSettings,
      [setting]: event.target.checked
    });
  };

  const handleSelectChange = (setting) => (event) => {
    setStbSettings({
      ...stbSettings,
      [setting]: event.target.value
    });
  };

  const handleNumericChange = (setting) => (event) => {
    setStbSettings({
      ...stbSettings,
      [setting]: parseInt(event.target.value) || 0
    });
  };

  return (
    <Box sx={{ margin: "20px 30px 0px 20px", overflow: "hidden" }}>
      {/* Header Section */}
      <Box sx={{ margin: "0px 0px 20px 0px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">
          <SecurityIcon fontSize="large" sx={{ verticalAlign: 'middle', mr: 1 }} />
          STB/Consumer Subscription Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {hasPermission(["OWNER"]) && (
            <>
              <Button variant="contained" startIcon={<ImportIcon />}>
                Import Config
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<ExportIcon />}
                onClick={() => handleDownload('excel')}
                disabled={downloading.excel}
              >
                {downloading.excel ? 'Generating...' : 'Export Excel'}
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload('pdf')}
                disabled={downloading.pdf}
              >
                {downloading.pdf ? 'Generating...' : 'Export PDF'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Configuration Accordion */}
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 1 }} />
            STB Configuration Settings
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 3,
            p: 2
          }}>
            {/* DRM Protection */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <SecurityIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                DRM Protection
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.drmProtection}
                    onChange={handleSettingChange('drmProtection')}
                    color="primary"
                  />
                }
                label="DRM Content Protection"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.decryption}
                    onChange={handleSettingChange('decryption')}
                    color="primary"
                  />
                }
                label="Decryption Support"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.copyProtection}
                    onChange={handleSettingChange('copyProtection')}
                    color="primary"
                  />
                }
                label="Copy Protection"
              />
            </Box>

            {/* Fingerprinting */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <FingerprintIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Fingerprinting
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Fingerprint Support</InputLabel>
                <Select
                  value={stbSettings.fingerprintSupport}
                  onChange={handleSelectChange('fingerprintSupport')}
                  label="Fingerprint Support"
                >
                  <MenuItem value="both">Both (Global & Targeted)</MenuItem>
                  <MenuItem value="global">Global Only</MenuItem>
                  <MenuItem value="targeted">Targeted Only</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.fingerprintAlwaysOn}
                    onChange={handleSettingChange('fingerprintAlwaysOn')}
                    color="primary"
                  />
                }
                label="Always Enabled"
              />
            </Box>

            {/* Messaging */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <MessageIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Messaging
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.messagingSupport}
                    onChange={handleSettingChange('messagingSupport')}
                    color="primary"
                  />
                }
                label="Messaging Support"
              />
              <TextField
                label="Min Character Length"
                type="number"
                value={stbSettings.messagingLength}
                onChange={handleNumericChange('messagingLength')}
                size="small"
                sx={{ width: '120px', mt: 1, mr: 2 }}
                inputProps={{ min: 100, max: 200 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.scrollMessaging}
                    onChange={handleSettingChange('scrollMessaging')}
                    color="primary"
                  />
                }
                label="Scroll Messaging"
              />
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Messaging Position</InputLabel>
                <Select
                  value={stbSettings.messagingPosition}
                  onChange={handleSelectChange('messagingPosition')}
                  label="Messaging Position"
                >
                  <MenuItem value="bottom">Lower Screen</MenuItem>
                  <MenuItem value="top">Upper Screen</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Network & Security */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <WifiIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Network & Security
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.addressability === 'individual'}
                    onChange={(e) => handleSelectChange('addressability')({ target: { value: e.target.checked ? 'individual' : 'group' } })}
                    color="primary"
                  />
                }
                label="Individually Addressable"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.geoTagging}
                    onChange={handleSettingChange('geoTagging')}
                    color="primary"
                  />
                }
                label="Geo Tagging"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.directCommands}
                    onChange={handleSettingChange('directCommands')}
                    color="primary"
                  />
                }
                label="Direct DRM Commands"
              />
            </Box>

            {/* Access Control */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <LockIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Access Control
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.blockThirdParty}
                    onChange={handleSettingChange('blockThirdParty')}
                    color="primary"
                  />
                }
                label="Block 3rd Party Apps"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.restrictKeyAccess}
                    onChange={handleSettingChange('restrictKeyAccess')}
                    color="primary"
                  />
                }
                label="Restrict Key Access"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.blockPlayStore}
                    onChange={handleSettingChange('blockPlayStore')}
                    color="primary"
                  />
                }
                label="Block Play Store"
              />
            </Box>

            {/* Compliance & Updates */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <VerifiedIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                Compliance & Updates
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.bisCompliance}
                    onChange={handleSettingChange('bisCompliance')}
                    color="primary"
                  />
                }
                label="BIS Compliance"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={stbSettings.otaUpgrade}
                    onChange={handleSettingChange('otaUpgrade')}
                    color="primary"
                  />
                }
                label="OTA Upgrade Support"
              />
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Watermark Position</InputLabel>
                <Select
                  value={stbSettings.watermarkPosition}
                  onChange={handleSelectChange('watermarkPosition')}
                  label="Watermark Position"
                >
                  <MenuItem value="encoder">Encoder End</MenuItem>
                  <MenuItem value="stb">STB End</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gridColumn: { xs: '1', md: '1/-1' } }}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<SyncIcon />}
                  sx={{ minWidth: '200px' }}
                >
                  Apply Configuration
                </Button>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  startIcon={<UpdateIcon />}
                  sx={{ minWidth: '200px' }}
                >
                  Push OTA Update
                </Button>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Controls Section */}
      <Box sx={{ 
        display: "flex", 
        flexDirection: { xs: "column", md: "row" }, 
        gap: 2, 
        mb: 3,
        alignItems: { xs: "flex-start", md: "center" }
      }}>
        {/* Search Box */}
        <TextField
          size="small"
          placeholder="Search STBs..."
          InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          sx={{ width: { xs: "100%", md: "300px" } }}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {/* Compliance Filter */}
        <Box sx={{ 
          display: "flex", 
          gap: 1, 
          backgroundColor: "action.hover",
          p: 1,
          borderRadius: 1
        }}>
          <Typography variant="body1" sx={{ alignSelf: "center", mr: 1 }}>
            Filter:
          </Typography>
          <Chip label="All" color="default" clickable />
          <Chip label="Compliant" color="success" clickable />
          <Chip label="Non-Compliant" color="error" clickable />
          <Chip label="Needs Update" color="warning" clickable />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
          <Tooltip title="Send Message to Selected">
            <IconButton color="primary">
              <MessageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Run Compliance Check">
            <IconButton color="secondary">
              <VerifiedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Force Sync">
            <IconButton color="info">
              <SyncIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "auto" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 450px)" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <Tooltip key={header.id} title={header.full} arrow>
                    <TableCell width={header.width} sx={{ 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: header.width
                    }}>
                      {header.display}
                    </TableCell>
                  </Tooltip>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: "primary.main" }}>S</Avatar>
                      <Typography variant="body2">{row.stbId}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{row.macId}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.drmProtection} 
                      size="small" 
                      color={row.drmProtection === "Enabled" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.decryption} 
                      size="small" 
                      color={row.decryption === "Supported" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.fingerprintSupport} 
                      size="small" 
                      color={row.fingerprintSupport === "Both" ? "success" : "warning"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.addressability} 
                      size="small" 
                      color={row.addressability === "Individual" ? "success" : "warning"} 
                    />
                  </TableCell>
                  <TableCell>{row.messaging}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.bisCompliance} 
                      size="small" 
                      color={row.bisCompliance === "Compliant" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.otaUpgrade} 
                      size="small" 
                      color={row.otaUpgrade === "Supported" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.copyProtection} 
                      size="small" 
                      color={row.copyProtection === "Enabled" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.fingerprintAlwaysOn} 
                      size="small" 
                      color={row.fingerprintAlwaysOn === "Yes" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.geoTagged} 
                      size="small" 
                      color={row.geoTagged === "Yes" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>{row.commandSource}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.thirdPartyApps} 
                      size="small" 
                      color={row.thirdPartyApps === "Blocked" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>{row.keyAccess}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.playStoreAccess} 
                      size="small" 
                      color={row.playStoreAccess === "Blocked" ? "success" : "error"} 
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {row.complianceStatus === "Compliant" ? (
                        <VerifiedIcon color="success" fontSize="small" />
                      ) : row.complianceStatus === "Partially Compliant" ? (
                        <WarningIcon color="warning" fontSize="small" />
                      ) : (
                        <WarningIcon color="error" fontSize="small" />
                      )}
                      <Typography variant="body2">{row.complianceStatus}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      </Paper>

      {/* Compliance Notes */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          <SecurityIcon color="primary" sx={{ verticalAlign: 'middle', mr: 1 }} />
          STB/Consumer Subscription Requirements
        </Typography>
        <Typography variant="body2" paragraph>
          • All STBs must have DRM content protection and support content decryption, decoding, and DRM license evaluation.
        </Typography>
        <Typography variant="body2" paragraph>
          • STBs must support both targeted channel fingerprinting and global fingerprinting from Headend.
        </Typography>
        <Typography variant="body2" paragraph>
          • Each STB must be individually addressable from Head-end with messaging support (minimum 120 characters).
        </Typography>
        <Typography variant="body2" paragraph>
          • STBs must be compliant with applicable Bureau of Indian Standards and support OTA software upgrades.
        </Typography>
        <Typography variant="body2" paragraph>
          • STBs with recording capabilities must have international standard copy protection systems.
        </Typography>
        <Typography variant="body2" paragraph>
          • Fingerprinting must never be disabled and watermarking must be inserted at encoder end for pay channels.
        </Typography>
        <Typography variant="body2" paragraph>
          • STBs must take commands directly from DRM (not intermediate servers) and must be geo-tagged.
        </Typography>
        <Typography variant="body2" paragraph>
          • STBs must block third-party app downloads, browser access, and only receive authorization keys from IPTV system.
        </Typography>
        <Typography variant="body2" paragraph>
          • Play Store access must be blocked when functioning in IPTV network.
        </Typography>
      </Box>
    </Box>
  );
};

export default DrmStbManagement;